import { fetchTmsActionLog, fetchTmsActionTimeline } from '@/backend/tmsActionLogApi';

const getDefaultState = () => ({
  items: [],
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  loading: false,
  page: 1,
  perPage: 50,
  requestSeq: 0,
  filters: {
    cargo_trip_id: '',
    trip_id: '',
    direction: '',
    flow: '',
    kind: '',
    status: '',
    error_kind: '',
    source_service: '',
    date_from: '',
    date_to: '',
  },
  timelines: {},
});

const state = getDefaultState();
const namespaced = true;

const DEFAULT_FILTERS = { ...getDefaultState().filters };

function buildParams(filters) {
  return {
    cargo_trip_id: filters.cargo_trip_id || undefined,
    trip_id: filters.trip_id || undefined,
    direction: filters.direction || undefined,
    flow: filters.flow || undefined,
    kind: filters.kind || undefined,
    status: filters.status || undefined,
    error_kind: filters.error_kind || undefined,
    source_service: filters.source_service || undefined,
    date_from: filters.date_from || undefined,
    date_to: filters.date_to ? `${filters.date_to}T23:59:59` : undefined,
  };
}

const mutations = {
  SET_LOADING(state, value) { state.loading = value; },
  SET_PAGE(state, value) { state.page = value; },
  SET_FILTERS(state, filters) {
    state.filters = { ...DEFAULT_FILTERS, ...filters };
    state.page = 1;
  },
  RESET_FILTERS(state) {
    state.filters = { ...DEFAULT_FILTERS };
    state.page = 1;
  },
  SET_REQUEST_SEQ(state, value) { state.requestSeq = value; },
  SET_RESPONSE(state, response) {
    state.items = Array.isArray(response?.items) ? response.items : [];
    state.currentPage = Number.isFinite(response?.current_page) ? response.current_page : 1;
    state.totalPages = Number.isFinite(response?.total_pages) ? response.total_pages : 1;
    state.totalItems = Number.isFinite(response?.total_items) ? response.total_items : state.items.length;
  },
  SET_TIMELINE(state, { cargoTripId, items }) {
    state.timelines = { ...state.timelines, [cargoTripId]: { items: items || [], loading: false, error: false } };
  },
  SET_TIMELINE_LOADING(state, { cargoTripId, value }) {
    const current = state.timelines[cargoTripId] || { items: [], loading: false, error: false };
    state.timelines = { ...state.timelines, [cargoTripId]: { ...current, loading: value } };
  },
  SET_TIMELINE_ERROR(state, { cargoTripId, value }) {
    const current = state.timelines[cargoTripId] || { items: [], loading: false, error: false };
    state.timelines = { ...state.timelines, [cargoTripId]: { ...current, error: value } };
  },
};

const actions = {
  async getLog({ commit, state }, options = {}) {
    const force = Boolean(options.force);
    if (state.loading && !force) return;

    const requestSeq = state.requestSeq + 1;
    commit('SET_REQUEST_SEQ', requestSeq);
    commit('SET_LOADING', true);
    try {
      const data = await fetchTmsActionLog({
        page: state.page,
        per_page: state.perPage,
        ...buildParams(state.filters),
      });
      if (requestSeq === state.requestSeq) {
        commit('SET_RESPONSE', data);
      }
    } finally {
      if (requestSeq === state.requestSeq) {
        commit('SET_LOADING', false);
      }
    }
  },

  setPage({ commit, dispatch }, value) {
    commit('SET_PAGE', value);
    dispatch('getLog');
  },

  setFilters({ commit, dispatch }, filters) {
    commit('SET_FILTERS', filters);
    dispatch('getLog');
  },

  resetFilters({ commit, dispatch }) {
    commit('RESET_FILTERS');
    dispatch('getLog');
  },

  async loadTimeline({ commit, state }, { cargoTripId, force = false }) {
    if (!cargoTripId) return;
    const existing = state.timelines[cargoTripId];
    if (existing && existing.loading) return;
    if (existing && !existing.error && !force) return;
    commit('SET_TIMELINE_LOADING', { cargoTripId, value: true });
    try {
      const data = await fetchTmsActionTimeline(cargoTripId, { limit: 50 });
      commit('SET_TIMELINE', { cargoTripId, items: data });
    } catch (e) {
      commit('SET_TIMELINE_ERROR', { cargoTripId, value: true });
    } finally {
      commit('SET_TIMELINE_LOADING', { cargoTripId, value: false });
    }
  },
};

export default {
  state,
  mutations,
  actions,
  namespaced,
};
